import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import WorkflowMapper from "../components/WorkflowMapper";
import { usePrefersReducedMotion } from "../lib/useReveal";
import { MACHINES, PROOF } from "./six/machines";
import { M } from "./six/tokens";
import { RowsPanel, FramesPanel, LogPanel, TreePanel, WirePanel, CalendarPanel, LivePanel } from "./six/panels";
import { ADS } from "./six/assets";
import OpsReport from "./six/OpsReport";
import { BrandProvider, useBrand } from "./six/BrandContext";
import OfferCore from "./six/OfferCore";
import BuildingBlocks from "./six/BuildingBlocks";
import LeadMagnet from "./six/LeadMagnet";
import AdStudio from "./six/AdStudio";
import AxosIntro from "./six/AxosIntro";
import ReportFormats from "./six/ReportFormats";

/* ══════════════════════════════════════════════════════════════
   06 · THE MACHINE  (flagship)
   A continuous scroll-driven experience. Six machines, each one
   pinned to the viewport and advanced by the scroll itself, doing
   the work an owner actually cares about, then handing back a
   week where the team approves instead of executes.
   ══════════════════════════════════════════════════════════════ */

/* ─────────────── cinematic section entrances, scrubbed by scroll ───────────────
   Cross browser replacement for animation-timeline: view(). Sections rise,
   fade and settle as they slide into the viewport, tied to scroll position. */
function useCinematicSections(enabled) {
  useEffect(() => {
    if (!enabled) return;
    const els = Array.from(
      document.querySelectorAll('[data-snap]:not([id^="machine-"]):not([data-testid="six-hero"])')
    )
      .map((s) => s.firstElementChild)
      .filter(Boolean);
    let raf = 0;
    const paint = () => {
      raf = 0;
      const vh = window.innerHeight;
      els.forEach((el) => {
        const r = el.parentElement.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        const t = 1 - (r.top - vh * 0.42) / (vh * 0.58);
        const p = Math.max(0, Math.min(1, t));
        if (p >= 0.999) {
          el.style.opacity = "";
          el.style.transform = "";
        } else {
          el.style.opacity = String(0.05 + p * 0.95);
          el.style.transform = `translateY(${(1 - p) * 56}px) scale(${0.97 + p * 0.03})`;
        }
      });
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(paint);
    };
    paint();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      els.forEach((el) => {
        el.style.opacity = "";
        el.style.transform = "";
      });
    };
  }, [enabled]);
}

/* ─────────────── always-on next section hint ─────────────── */
function NextHint({ reduced }) {
  const [show, setShow] = useState(true);
  useEffect(() => {
    const on = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setShow(window.scrollY < max - window.innerHeight * 1.2);
    };
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const next = () => {
    let target = null;
    document.querySelectorAll("[data-snap]").forEach((el) => {
      const top = el.getBoundingClientRect().top;
      if (top > window.innerHeight * 0.35 && (target === null || top < target)) target = top;
    });
    window.scrollTo({
      top: window.scrollY + (target ?? window.innerHeight * 0.8),
      behavior: reduced ? "auto" : "smooth",
    });
  };
  if (!show) return null;
  return (
    <button
      onClick={next}
      data-testid="next-section-hint"
      aria-label="Next section"
      className="focus-ring fixed bottom-5 left-1/2 z-40 -translate-x-1/2 rounded-full border p-2.5 backdrop-blur-md"
      style={{ borderColor: M.border, background: "rgba(7,8,10,0.72)", color: M.dim }}
    >
      <ChevronDown size={15} style={{ animation: reduced ? "none" : "sixNudge 2.2s ease-in-out infinite" }} />
    </button>
  );
}

/* ─────────────── panel router ─────────────── */
function Stage({ machine, step }) {
  const s = machine.steps[step];
  const common = { accent: machine.accent, step };
  const bare = s.kind === "live" || s.kind === "report" || s.kind === "formats";
  return (
    <div
      className="overflow-hidden rounded-2xl border"
      style={{ borderColor: M.border, background: M.surface, boxShadow: "0 30px 80px rgba(0,0,0,0.6)" }}
      data-testid="machine-stage"
    >
      <div className="flex items-center gap-3 border-b px-4 py-2.5" style={{ borderColor: M.border, background: M.surface2 }}>
        <span className="h-2 w-2 rounded-full" style={{ background: machine.accent, boxShadow: `0 0 10px ${machine.accent}` }} />
        <span className="f-mono text-[9.5px] uppercase tracking-[0.16em]" style={{ color: M.muted }}>{machine.account}</span>
        <span className="f-mono ml-auto text-[9.5px]" style={{ color: M.dim }}>
          {String(step + 1).padStart(2, "0")} / 0{machine.steps.length}
        </span>
      </div>
      <div className={bare ? "" : "p-4 sm:p-5"} style={{ minHeight: 360 }}>
        <div key={step} style={{ animation: "stageIn 460ms cubic-bezier(0.16,1,0.3,1) both" }}>
          {s.kind === "rows" && <RowsPanel {...common} data={s.data} />}
          {s.kind === "frames" && <FramesPanel {...common} data={s.data} />}
          {s.kind === "log" && <LogPanel {...common} data={s.data} />}
          {s.kind === "tree" && <TreePanel {...common} data={s.data} />}
          {s.kind === "wire" && <WirePanel {...common} data={s.data} />}
          {s.kind === "cal" && <CalendarPanel {...common} data={s.data} />}
          {s.kind === "live" && <LivePanel {...common} data={s.data} />}
          {s.kind === "report" && <OpsReport embedded />}
          {s.kind === "formats" && <ReportFormats accent={machine.accent} />}
        </div>
      </div>
    </div>
  );
}

/* ─────────────── how this machine moves the chosen number ─────────────── */
function KpiLine({ machine }) {
  const { offerMode, kpiLabel } = useBrand();
  if (!offerMode) {
    return (
      <a
        href="#offer-core"
        data-testid={`kpi-prompt-${machine.id}`}
        className="focus-ring f-mono mt-4 inline-block text-[9.5px] uppercase tracking-[0.14em] underline decoration-dotted underline-offset-4"
        style={{ color: M.dim }}
      >
        Pick LTV or AOV above to see how this one moves your number
      </a>
    );
  }
  return (
    <div
      className="mt-4 border-l pl-3.5"
      style={{ borderColor: machine.accent }}
      data-testid={`kpi-line-${machine.id}`}
    >
      <span className="f-mono block text-[9px] uppercase tracking-[0.16em]" style={{ color: machine.accent }}>
        Moves {kpiLabel}
      </span>
      <span className="mt-1 block max-w-[52ch] text-[13px] leading-[1.65]" style={{ color: M.ink }}>
        {machine.kpi?.[offerMode]}
      </span>
    </div>
  );
}

/* ─────────────── one big proof number per machine ─────────────── */
function MachineStat({ machine }) {
  if (!machine.stat) return null;
  return (
    <div className="mt-5 flex items-baseline gap-3" data-testid={`machine-stat-${machine.id}`}>
      <span className="f-grotesk tnum text-[28px] font-bold leading-none sm:text-[34px]" style={{ color: machine.accent }}>
        {machine.stat.v}
      </span>
      <span className="f-mono text-[9px] uppercase tracking-[0.14em] sm:text-[9.5px]" style={{ color: M.muted }}>
        {machine.stat.l}
      </span>
    </div>
  );
}

function useIsDesktop() {
  const [d, setD] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 1024px)");
    setD(mq.matches);
    const on = (e) => setD(e.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return d;
}

/* ─────────────── one pinned machine scene ─────────────── */
export const MachineScene = React.memo(function MachineScene({ machine, index, onActive }) {
  const steps = machine.steps.length;
  const isDesktop = useIsDesktop();
  const ref = useRef(null);
  const openerRef = useRef(null);
  const contentRef = useRef(null);
  const railRef = useRef(null);
  const [ii, setIi] = useState(0);

  /* scroll scrub writes styles straight to the DOM, React only re-renders on step change */
  useEffect(() => {
    if (!isDesktop) return;
    const OPEN = 0.12;
    let raf = 0;
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      if (r.bottom < -vh || r.top > vh * 2) return;
      const total = Math.max(1, r.height - vh);
      const p = Math.min(Math.max(-r.top, 0), total) / total;
      if (r.top < vh * 0.6 && r.bottom > vh * 0.4) onActive(index);
      const ap = Math.max(0, Math.min(1, (p - OPEN) / (1 - OPEN)));
      const k = Math.min(steps - 1, Math.floor(ap * steps * 0.9999));
      setIi((prev) => (prev === k ? prev : k));
      const openerO = Math.max(0, Math.min(1, 1 - (p - 0.05) / 0.055));
      const contentO = Math.max(0, Math.min(1, (p - 0.05) / 0.07));
      const exit = Math.max(0, Math.min(1, (p - 0.93) / 0.07));
      if (openerRef.current) {
        const o = openerRef.current;
        o.style.opacity = String(openerO);
        o.style.transform = `scale(${1 + (1 - openerO) * 0.07}) translateY(${(1 - openerO) * -22}px)`;
        o.style.visibility = openerO < 0.02 ? "hidden" : "visible";
      }
      if (contentRef.current) {
        const c = contentRef.current;
        c.style.opacity = String(contentO * (1 - exit * 0.65));
        c.style.transform = `translateY(${(1 - contentO) * 26 - exit * 18}px) scale(${1 - exit * 0.02})`;
      }
      if (railRef.current) {
        const frac = Math.min(100, ((ap * steps) % 1) * 100);
        Array.from(railRef.current.children).forEach((track, t) => {
          const fill = track.firstElementChild;
          if (fill) fill.style.width = t < k ? "100%" : t === k ? `${frac}%` : "0%";
        });
      }
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isDesktop, steps, index, onActive]);

  /* below lg the scroll-pin degrades into an honest stacked read */
  if (!isDesktop) {
    return (
      <section ref={ref} data-testid={`machine-section-${machine.id}`} id={`machine-${machine.id}`} className="border-b" style={{ borderColor: M.border }}>
        <div className="mx-auto w-full max-w-[1280px] px-5 py-14">
          <div className="mb-5 flex items-center gap-3">
            <span className="f-mono text-[10px]" style={{ color: machine.accent }}>M{String(index + 1).padStart(2, "0")}</span>
            <span className="h-px flex-1" style={{ background: M.border }} />
            <span className="f-mono text-[9.5px] uppercase tracking-[0.2em]" style={{ color: M.muted }}>{machine.name}</span>
          </div>
          <h2 className="f-grotesk text-[30px] font-bold leading-[1.03] tracking-[-0.025em]">{machine.headline}</h2>
          <p className="mt-4 text-[14px] leading-[1.7]" style={{ color: M.muted }}>{machine.sub}</p>
          <MachineStat machine={machine} />
          <KpiLine machine={machine} />
          <div className="mt-9 space-y-10">
            {machine.steps.map((st, k) => (
              <div key={k}>
                <div className="f-mono mb-2 text-[9.5px] uppercase tracking-[0.18em]" style={{ color: machine.accent }}>{st.tag}</div>
                <h3 className="f-grotesk text-[20px] font-semibold leading-snug">{st.title}</h3>
                <p className="mt-3 text-[14px] leading-[1.75]" style={{ color: M.muted }}>{st.body}</p>
                <div className="mt-5">
                  <Stage machine={machine} step={k} />
                </div>
                <div className="f-mono mt-3 text-[9.5px] uppercase tracking-[0.14em]" style={{ color: M.dim }}>{st.foot}</div>
              </div>
            ))}
          </div>
          {machine.magnet && (
            <div className="mt-8">
              <LeadMagnet machineId={machine.id} label={machine.magnet.label} asset={machine.magnet.asset} accent={machine.accent} />
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} style={{ height: `${steps * 62 + 18}vh` }} data-snap data-testid={`machine-section-${machine.id}`} id={`machine-${machine.id}`}>
      <div className="sticky top-0 flex min-h-screen items-center overflow-hidden">
        {/* atmosphere */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: `radial-gradient(80% 60% at 82% 12%, ${machine.accent}14, transparent 62%)` }}
        />

        {/* opener title card */}
        <div
          ref={openerRef}
          className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center"
          data-testid={`machine-opener-${machine.id}`}
        >
          <div className="text-center">
            <div className="f-mono text-[11px] uppercase tracking-[0.34em]" style={{ color: M.muted }}>
              Machine {String(index + 1).padStart(2, "0")} · of 06
            </div>
            <div
              className="f-grotesk mt-2 text-[96px] font-bold uppercase leading-[0.95] tracking-[-0.04em] lg:text-[150px]"
              style={{ color: machine.accent, textShadow: `0 0 110px ${machine.accent}66` }}
            >
              {machine.name}
            </div>
            <div className="f-mono mt-5 text-[10px] uppercase tracking-[0.26em]" style={{ color: M.dim }}>
              {machine.stat ? `${machine.stat.v} · ${machine.stat.l}` : "keep scrolling"}
            </div>
          </div>
        </div>

        <div
          ref={contentRef}
          className="relative mx-auto grid w-full max-w-[1280px] items-center gap-8 px-5 py-14 sm:px-8 lg:grid-cols-[0.92fr_1.08fr] lg:gap-14"
          style={{ opacity: 0 }}
        >
          {/* copy */}
          <div className="min-w-0">
            <div className="mb-6 flex items-center gap-3">
              <span className="f-mono text-[10px]" style={{ color: machine.accent }}>M{String(index + 1).padStart(2, "0")}</span>
              <span className="h-px flex-1" style={{ background: M.border }} />
              <span className="f-mono text-[9.5px] uppercase tracking-[0.2em]" style={{ color: M.muted }}>{machine.name}</span>
            </div>

            <h2 className="f-grotesk text-[30px] font-bold leading-[1.02] tracking-[-0.025em] sm:text-[40px] lg:text-[46px]">
              {machine.headline}
            </h2>
            <MachineStat machine={machine} />
            <KpiLine machine={machine} />

            {/* step copy, big ghost numeral, crossfades as you scroll */}
            <div className="relative mt-10" style={{ minHeight: 150 }}>
              <div
                aria-hidden
                key={`n-${ii}`}
                className="f-grotesk pointer-events-none absolute -top-16 right-0 select-none text-[120px] font-bold leading-none"
                style={{ color: machine.accent, opacity: 0.07, animation: "stageIn 520ms cubic-bezier(0.16,1,0.3,1) both" }}
              >
                {String(ii + 1).padStart(2, "0")}
              </div>
              {machine.steps.map((st, k) => (
                <div
                  key={k}
                  className="absolute inset-0"
                  style={{
                    opacity: k === ii ? 1 : 0,
                    transform: k === ii ? "translateY(0)" : `translateY(${k < ii ? -16 : 16}px)`,
                    transition: "opacity 340ms cubic-bezier(0.16,1,0.3,1), transform 340ms cubic-bezier(0.16,1,0.3,1)",
                    pointerEvents: k === ii ? "auto" : "none",
                  }}
                  aria-hidden={k !== ii}
                >
                  <div className="mb-3 flex items-center gap-2.5">
                    <span
                      className="f-mono rounded-full border px-2.5 py-1 text-[8.5px] uppercase tracking-[0.14em]"
                      style={{ color: machine.accent, borderColor: `${machine.accent}55`, background: `${machine.accent}0e` }}
                    >
                      {st.tag}
                    </span>
                    <span className="f-mono text-[8.5px]" style={{ color: M.dim }}>{String(k + 1).padStart(2, "0")} / 0{steps}</span>
                  </div>
                  <h3 className="f-grotesk text-[24px] font-bold leading-[1.12] tracking-[-0.02em] sm:text-[28px]">{st.title}</h3>
                  <p className="mt-2.5 max-w-[46ch] text-[13.5px] leading-[1.7]" style={{ color: M.muted }}>{st.body}</p>
                </div>
              ))}
            </div>

            {/* step rail */}
            <div ref={railRef} className="mt-6 flex gap-2" data-testid={`machine-rail-${machine.id}`}>
              {machine.steps.map((_, k) => (
                <div key={k} className="h-[3px] flex-1 overflow-hidden rounded-full" style={{ background: M.border }}>
                  <div className="h-full rounded-full" style={{ width: "0%", background: machine.accent }} />
                </div>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <div className="f-mono text-[9.5px] uppercase tracking-[0.14em]" style={{ color: M.dim }}>
                {machine.steps[ii].foot}
              </div>
              {machine.magnet && (
                <div className="ml-auto">
                  <LeadMagnet machineId={machine.id} label={machine.magnet.label} asset={machine.magnet.asset} accent={machine.accent} />
                </div>
              )}
            </div>
          </div>

          {/* stage */}
          <div className="min-w-0">
            <Stage machine={machine} step={ii} />
          </div>
        </div>
      </div>
    </section>
  );
});

/* ─────────────── approval inbox climax ─────────────── */
const INBOX = [
  { t: "Publish the new bundle across the storefront", why: "Customer-facing. Never automated.", risk: "publish" },
  { t: "Change the sitewide offer for the weekend", why: "Strategy. This is authorship, not throughput.", risk: "strategy" },
  { t: "Increase the prospecting ceiling by $6k/wk", why: "Material spend. Above the standing limit.", risk: "spend" },
  { t: "Approve the claim in creative variant HERO-A14c", why: "New product claim not in the approved library.", risk: "brand" },
  { t: "Decide: slow retargeting or accept the stockout", why: "A trade-off with no correct answer. Yours.", risk: "judgment" },
];

function ApprovalInbox() {
  const [done, setDone] = useState([]);
  const all = done.length === INBOX.length;
  return (
    <div className="rounded-2xl border" style={{ borderColor: M.border, background: M.surface }} data-testid="approval-inbox">
      <div className="flex flex-wrap items-center gap-3 border-b px-5 py-3.5" style={{ borderColor: M.border, background: M.surface2 }}>
        <span className="f-mono text-[9.5px] uppercase tracking-[0.16em]" style={{ color: M.muted }}>Your week · approvals only</span>
        <span className="f-mono ml-auto text-[10px]" style={{ color: all ? M.ok : M.warn }}>
          {INBOX.length - done.length} waiting on a person
        </span>
        {done.length > 0 && (
          <button onClick={() => setDone([])} data-testid="inbox-reset" className="focus-ring f-mono rounded px-2.5 py-1 text-[9.5px]" style={{ border: `1px solid ${M.border}`, color: M.dim }}>
            reset
          </button>
        )}
      </div>
      <div>
        {INBOX.map((x, i) => {
          const cleared = done.includes(i);
          return (
            <div
              key={x.t}
              className="flex flex-wrap items-center gap-4 border-b px-5 py-4 last:border-b-0 transition-colors"
              style={{ borderColor: M.border, background: cleared ? "rgba(95,227,161,0.05)" : "transparent" }}
              data-testid={`inbox-item-${i}`}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] leading-snug" style={{ color: cleared ? M.muted : M.ink, textDecoration: cleared ? "line-through" : "none" }}>
                  {x.t}
                </span>
                <span className="f-mono mt-1 block text-[9.5px] uppercase tracking-[0.12em]" style={{ color: M.dim }}>
                  {x.risk} · {x.why}
                </span>
              </span>
              {cleared ? (
                <span className="f-mono text-[10px] uppercase tracking-[0.12em]" style={{ color: M.ok }}>cleared · executing</span>
              ) : (
                <button
                  onClick={() => setDone((d) => [...d, i])}
                  data-testid={`inbox-approve-${i}`}
                  className="focus-ring f-mono rounded-lg px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.12em]"
                  style={{ background: M.ok, color: "#04170F" }}
                >
                  Approve
                </button>
              )}
            </div>
          );
        })}
      </div>
      {all && (
        <div className="border-t px-5 py-6" style={{ borderColor: M.border }} data-testid="inbox-complete">
          <div className="f-grotesk text-[22px] font-bold sm:text-[26px]">That was your week.</div>
          <p className="mt-2 max-w-[56ch] text-[13.5px] leading-[1.75]" style={{ color: M.muted }}>
            Five decisions. Everything else shipped, measured, logged and reversible.
          </p>
        </div>
      )}
    </div>
  );
}

/* ─────────────── real ads peeking behind the hero board ─────────────── */
function HeroAds({ reduced }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => setI((x) => (x + 1) % ADS.length), 4200);
    return () => clearInterval(id);
  }, [reduced]);
  return (
    <>
      <img
        key={`l-${i}`}
        src={ADS[i]}
        alt="Axos static ad"
        data-testid="hero-ad-left"
        className="pointer-events-none absolute -left-16 top-4 hidden w-[146px] rounded-xl border object-cover xl:block"
        style={{ borderColor: M.border, aspectRatio: "3 / 4", transform: "rotate(-7deg)", boxShadow: "0 24px 60px rgba(0,0,0,0.55)", animation: reduced ? "none" : "sixIn 600ms cubic-bezier(0.16,1,0.3,1) both" }}
      />
      <img
        key={`r-${i}`}
        src={ADS[(i + 4) % ADS.length]}
        alt="Axos static ad"
        data-testid="hero-ad-right"
        className="pointer-events-none absolute -right-12 bottom-8 hidden w-[132px] rounded-xl border object-cover xl:block"
        style={{ borderColor: M.border, aspectRatio: "3 / 4", transform: "rotate(6deg)", boxShadow: "0 24px 60px rgba(0,0,0,0.55)", animation: reduced ? "none" : "sixIn 600ms cubic-bezier(0.16,1,0.3,1) 120ms both" }}
      />
    </>
  );
}

/* ─────────────── page ─────────────── */
function TheMachine() {
  const [activeMachine, setActiveMachine] = useState(0);
  const reduced = usePrefersReducedMotion();
  const isDesktop = useIsDesktop();
  const { agentName } = useBrand();
  const heroRef = useRef(null);
  const heroInnerRef = useRef(null);
  const heroGridRef = useRef(null);
  const progressRef = useRef(null);

  /* progress bar + hero parallax write straight to the DOM, no re-renders while scrolling */
  useEffect(() => {
    const on = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      if (progressRef.current) progressRef.current.style.width = `${max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 0}%`;
      const el = heroRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const hp = Math.min(1, Math.max(0, -r.top / Math.max(1, r.height * 0.8)));
      if (heroGridRef.current) heroGridRef.current.style.opacity = String(0.5 - hp * 0.4);
      if (heroInnerRef.current) {
        heroInnerRef.current.style.opacity = String(1 - hp * 1.25);
        heroInnerRef.current.style.transform = reduced ? "none" : `translateY(${-hp * 60}px)`;
      }
    };
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, [reduced]);

  const jump = (id) => document.getElementById(`machine-${id}`)?.scrollIntoView({ behavior: reduced ? "auto" : "smooth" });

  useCinematicSections(isDesktop && !reduced);

  const sim = useMemo(() => ({ direction: "the-machine", machines_shown: MACHINES.length }), []);

  return (
    <div className="min-h-screen" style={{ background: M.bg, color: M.ink }}>

      {/* progress + machine rail */}
      <div className="fixed left-0 right-0 top-0 z-50">
        <div className="h-[2px] w-full" style={{ background: M.border }}>
          <div ref={progressRef} className="h-full" style={{ width: "0%", background: MACHINES[activeMachine]?.accent || M.ok, transition: "background 400ms linear" }} />
        </div>
        <div className="border-b backdrop-blur-md" style={{ borderColor: M.border, background: "rgba(7,8,10,0.82)" }}>
          <div className="mx-auto flex w-full max-w-[1280px] items-center gap-3 px-5 py-2.5 sm:px-8">
            <span className="f-grotesk text-[12.5px] font-bold tracking-[-0.01em]" data-testid="nav-brand">{agentName.toUpperCase()}</span>
            <div className="ml-2 hidden items-center gap-1.5 lg:flex" data-testid="machine-nav">
              {MACHINES.map((m, i) => (
                <button
                  key={m.id}
                  onClick={() => jump(m.id)}
                  data-testid={`machine-nav-${m.id}`}
                  className="focus-ring f-mono rounded px-2.5 py-1.5 text-[9.5px] uppercase tracking-[0.1em] transition-colors"
                  style={{
                    color: i === activeMachine ? m.accent : M.dim,
                    border: `1px solid ${i === activeMachine ? m.accent : "transparent"}`,
                  }}
                >
                  {m.name}
                </button>
              ))}
            </div>
            <a href="#map" className="focus-ring f-mono ml-auto rounded px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.12em]" style={{ background: M.ink, color: "#0A0C10" }}>
              Map a workflow
            </a>
          </div>
        </div>
      </div>

      {/* HERO, scroll reactive */}
      <section ref={heroRef} style={{ height: "150vh" }} data-snap data-testid="six-hero">
        <div className="sticky top-0 flex h-screen items-center overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: "radial-gradient(70% 55% at 50% 8%, rgba(95,227,161,0.10), transparent 62%)" }}
          />
          <div ref={heroGridRef} className="grid-fine pointer-events-none absolute inset-0" style={{ opacity: 0.5 }} />
          <div className="relative mx-auto w-full max-w-[1280px] px-5 sm:px-8">
            <div
              ref={heroInnerRef}
              className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14"
            >
              <div style={{ animation: reduced ? "none" : "sixIn 700ms cubic-bezier(0.16,1,0.3,1) both" }}>
                <div className="f-mono mb-7 text-[10px] uppercase tracking-[0.24em]" style={{ color: M.ok }} data-testid="hero-eyebrow">
                  {agentName} · for established ecommerce brands
                </div>
                <h1 className="f-grotesk max-w-[19ch] text-[40px] font-bold leading-[0.98] tracking-[-0.03em] sm:text-[62px] lg:text-[70px]">
                  <span className="block" style={{ animation: reduced ? "none" : "sixIn 700ms cubic-bezier(0.16,1,0.3,1) 100ms both" }}>
                    An in-house agent
                  </span>
                  <span style={{ color: M.ok, animation: reduced ? "none" : "sixIn 700ms cubic-bezier(0.16,1,0.3,1) 240ms both" }}>
                    that actually completes the work.
                  </span>
                </h1>
                <p
                  className="mt-7 max-w-[52ch] text-[15px] leading-[1.75] sm:text-[17px]"
                  style={{ color: M.muted, animation: reduced ? "none" : "sixIn 700ms cubic-bezier(0.16,1,0.3,1) 380ms both" }}
                >
                  Monday: 50 new ads live. Tuesday: a new page test. Friday: a new offer in market. It runs the accounts.
                  It does not send you a dashboard about them.
                </p>
                <div
                  className="mt-8 flex flex-wrap items-center gap-4"
                  style={{ animation: reduced ? "none" : "sixIn 700ms cubic-bezier(0.16,1,0.3,1) 500ms both" }}
                >
                  <button
                    onClick={() => jump(MACHINES[0].id)}
                    data-testid="six-hero-cta"
                    className="focus-ring f-mono rounded px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.14em]"
                    style={{ background: M.ok, color: "#04170F" }}
                  >
                    Start scrolling
                  </button>
                  <span className="f-mono text-[10px] uppercase tracking-[0.16em]" style={{ color: M.dim }}>
                    the board is the week it runs, live
                  </span>
                </div>
              </div>

              {/* the living Monday report, the hero demo */}
              {isDesktop && (
                <div className="relative min-w-0" style={{ animation: reduced ? "none" : "sixIn 800ms cubic-bezier(0.16,1,0.3,1) 300ms both" }}>
                  <HeroAds reduced={reduced} />
                  <div className="relative min-w-0">
                    <OpsReport />
                  </div>
                  <p className="f-mono relative mt-3 text-center text-[9px] uppercase tracking-[0.16em]" style={{ color: M.dim }}>
                    One thing a day · click through the week
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* the report again, for narrow screens where the hero cannot hold it */}
      {!isDesktop && (
        <section className="border-t" style={{ borderColor: M.border, background: M.deep }}>
          <div className="mx-auto w-full max-w-[1280px] px-5 py-12">
            <OpsReport />
          </div>
        </section>
      )}

      {/* THE OFFER CORE (the fork), then straight into the machines */}
      <OfferCore />

      {/* WHO THE EXAMPLE IS, before any of the work is shown */}
      <AxosIntro />

      {/* THE SIX MACHINES */}
      {MACHINES.map((m, i) => (
        <MachineScene key={m.id} machine={m} index={i} onActive={setActiveMachine} />
      ))}

      {/* AD STUDIO (hidden until Higgsfield is configured) */}
      <AdStudio />

      {/* THE BLOCK SYSTEM, after they have seen the work */}
      <BuildingBlocks />

      {/* CLIMAX */}
      <section className="border-y" style={{ borderColor: M.border, background: M.deep }} data-snap>
        <div className="mx-auto grid w-full max-w-[1280px] gap-10 px-5 py-16 sm:px-8 sm:py-24 lg:grid-cols-[0.9fr_1.1fr] lg:gap-16">
          <div>
            <div className="f-mono mb-4 text-[10px] uppercase tracking-[0.22em]" style={{ color: M.warn }}>What is left for you</div>
            <h2 className="f-grotesk text-[30px] font-bold leading-[1.04] tracking-[-0.025em] sm:text-[46px]">
              Your team stops doing the work and starts approving it.
            </h2>
            <p className="mt-6 max-w-[54ch] text-[15px] leading-[1.8]" style={{ color: M.muted }}>
              The machines do not remove judgment. They remove everything that was standing between your judgment and a live
              result. Publishing, material spend, brand claims and real trade-offs still come to a person.
            </p>
          </div>
          <ApprovalInbox />
        </div>
      </section>

      {/* MAP */}
      <section id="map" data-snap>
        <div className="mx-auto w-full max-w-[1280px] px-5 py-16 sm:px-8 sm:py-24">
          <h2 className="f-grotesk mb-4 max-w-[24ch] text-[30px] font-bold leading-[1.05] tracking-[-0.025em] sm:text-[46px]">
            Pick the machine you want first.
          </h2>
          <p className="mb-10 max-w-[60ch] text-[15px] leading-[1.8]" style={{ color: M.muted }}>
            Six machines is where this ends up. It starts with one expensive recurring thing, done by hand, tuned, then left alone.
          </p>
          <WorkflowMapper
            directionId="six"
            directionName="THE MACHINE"
            simulationSummary={sim}
            eyebrow="First machine · written map back"
            title="Which one would change your week the most?"
            blurb="Describe the recurring work you would hand over first. You get a written map back: what it needs, what stays human, and what it has to prove before it runs on its own."
            theme={{
              bg: M.bg, panel: M.surface, field: M.surface2, ink: M.ink, muted: M.muted,
              accent: M.ok, accentInk: "#04170F", border: M.border, radius: "12px",
              fontDisplay: "f-grotesk font-bold", fontLabel: "f-mono",
            }}
          />
        </div>
      </section>

      <footer className="border-t" style={{ borderColor: M.border }}>
        <div className="mx-auto flex w-full max-w-[1280px] flex-wrap items-center gap-4 px-5 py-9 sm:px-8">
          <span className="f-grotesk text-[13px] font-bold">{agentName.toUpperCase()}</span>
          <span className="f-mono text-[10px] uppercase tracking-[0.16em]" style={{ color: M.muted }}>
            The Machine · operator led, built one workflow at a time
          </span>
        </div>
      </footer>

      {isDesktop && <NextHint reduced={reduced} />}
    </div>
  );
}

export default function DirectionSix() {
  return (
    <BrandProvider>
      <TheMachine />
    </BrandProvider>
  );
}


